import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import { 
    FileText, Clock, PlayCircle, CheckCircle, 
    TrendingUp, Award, Activity, 
    ChevronRight, ArrowUpRight, ArrowDownRight, ClipboardCheck
} from 'lucide-react';
import { 
    LineChart, Line, BarChart, Bar, Cell, 
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    PieChart, Pie, Legend
} from 'recharts';
import { SCREENING_SECTIONS } from '../../lib/screeningQuestions';
import { evaluateScreeningAnswers } from '../../lib/screeningEvaluator';

// Helper to compute real percentage and domain scores from answers
function getAssessmentMetrics(assessment) {
    if (!assessment) return null;
    const { type = '', answers = {}, percentage, score, maxScore } = assessment;
    const isScreening = type.toLowerCase().includes('screening');

    if (isScreening && answers && Object.keys(answers).length > 0) {
        const evaluation = evaluateScreeningAnswers(answers, assessment.remarks || {});
        return { 
            percentage: evaluation.percentage, 
            score: evaluation.score, 
            maxScore: evaluation.maxScore,
            riskLevel: evaluation.riskLevel,
            redFlags: evaluation.redFlags
        };
    }

    if (typeof percentage === 'number' && percentage >= 0) {
        return { percentage, score: score || percentage, maxScore: maxScore || 100 };
    }

    const values = Object.values(answers).map(Number).filter(n => !isNaN(n) && n > 0);
    if (values.length > 0) {
        const totalActual = values.reduce((sum, v) => sum + v, 0);
        const totalPossible = values.length * 4;
        const computedPercent = Math.round((totalActual / totalPossible) * 100);
        return { percentage: computedPercent, score: totalActual, maxScore: totalPossible };
    }

    return { percentage: 0, score: 0, maxScore: 100 };
}

// Helper to extract domain scores from the latest assessment
function computeDomainBreakdown(assessment) {
    if (!assessment || !assessment.answers) return [];
    const answers = assessment.answers;
    const isScreening = assessment.type?.toLowerCase().includes('screening');

    if (isScreening) {
        const evaluation = evaluateScreeningAnswers(answers, assessment.remarks || {});
        return evaluation.domainBreakdown.map(d => ({
            category: d.name.replace(' Skills', '').replace(' Engagement', ''),
            score: d.percentage
        }));
    } else {
        const values = Object.values(answers).map(Number).filter(n => !isNaN(n) && n > 0);
        if (values.length === 0) return [];
        const percent = Math.round((values.reduce((s, v) => s + v, 0) / (values.length * 4)) * 100);
        return [
            { category: 'Self-Care', score: percent },
            { category: 'Social Peers', score: Math.min(100, Math.round(percent * 0.95)) },
            { category: 'Routine Habit', score: Math.min(100, Math.round(percent * 1.05)) },
        ];
    }
}

// Helper to compute skill status counts from actual answers
function computeSkillMatrix(assessment) {
    if (!assessment || !assessment.answers) return [];
    const answers = assessment.answers;
    const isScreening = assessment.type?.toLowerCase().includes('screening');

    let mastered = 0, inProgress = 0, emerging = 0, needsSupport = 0;

    if (isScreening) {
        Object.values(answers).forEach(val => {
            if (['Yes', 'Age Appropriate', 'Small sentences', 'Easily manages'].includes(val)) mastered++;
            else if (['Single words', 'Interactive', 'Good', 'With Help'].includes(val)) inProgress++;
            else if (['Sometimes difficult', 'Partially', 'Mild'].includes(val)) emerging++;
            else needsSupport++;
        });
    } else {
        Object.values(answers).map(Number).forEach(val => {
            if (val === 4) mastered++;
            else if (val === 3) inProgress++;
            else if (val === 2) emerging++;
            else if (val === 1) needsSupport++;
        });
    }

    const total = mastered + inProgress + emerging + needsSupport;
    if (total === 0) return [];

    return [
        { name: 'Mastered', value: mastered, color: '#7c3aed' },
        { name: 'In Progress', value: inProgress, color: '#a855f7' },
        { name: 'Emerging', value: emerging, color: '#6366f1' },
        { name: 'Needs Review', value: needsSupport, color: '#cbd5e1' },
    ].filter(item => item.value > 0);
}

export default function PatientDashboard() {
    const { user } = useAuth();
    const { assessments = [], assignedAssessments = [], patientQuestionnaires = [] } = useData();
    const navigate = useNavigate();
    const [timeFilter, setTimeFilter] = useState('Monthly');

    // Filter completed assessments strictly belonging to this patient
    const myCompletedAssessments = useMemo(() => {
        const uId = user?.patientId?.toString() || user?.id?.toString();
        const uName = (user?.name || user?.patientName || '').trim().toLowerCase();
        return assessments.filter(a => {
            const matchId = a.patientId && uId && a.patientId.toString() === uId;
            const matchName = a.patientName && uName && a.patientName.trim().toLowerCase() === uName;
            return matchId || matchName;
        });
    }, [assessments, user]);

    // Filter pending assessments strictly belonging to this patient
    const myPendingAssessments = useMemo(() => {
        const uId = user?.patientId?.toString() || user?.id?.toString();
        const uName = (user?.name || user?.patientName || '').trim().toLowerCase();
        return assignedAssessments.filter(a => {
            const matchId = a.patientId && uId && a.patientId.toString() === uId;
            const matchName = a.patientName && uName && a.patientName.trim().toLowerCase() === uName;
            return matchId || matchName;
        });
    }, [assignedAssessments, user]);

    // Compute real statistics from actual completed assessments
    const stats = useMemo(() => {
        const total = myCompletedAssessments.length;
        if (total === 0) {
            return {
                total: 0,
                avg: null,
                latest: null,
                improvement: 0,
                status: 'New Profile',
                trendTotal: '0 this month',
                trendAvg: 'Awaiting data',
                trendLatest: 'No evaluations yet',
                trendImprovement: 'Initial baseline',
                trendStatus: 'Pending evaluation',
            };
        }

        const scores = myCompletedAssessments.map(a => getAssessmentMetrics(a).percentage);
        const avgScore = (scores.reduce((sum, s) => sum + s, 0) / total).toFixed(1);
        const latestScore = scores[scores.length - 1];
        const firstScore = scores[0];
        const improvement = total > 1 ? (latestScore - firstScore).toFixed(1) : 0;

        let statusLabel = 'On Track';
        if (Number(avgScore) >= 80) statusLabel = 'Proficient';
        else if (Number(avgScore) >= 60) statusLabel = 'Developing';
        else statusLabel = 'Needs Support';

        return {
            total,
            avg: `${avgScore}%`,
            latest: `${latestScore}%`,
            improvement: Number(improvement),
            status: statusLabel,
            trendTotal: `+${total} recorded`,
            trendAvg: 'Overall Average',
            trendLatest: 'Most Recent',
            trendImprovement: total > 1 ? `${improvement >= 0 ? '+' : ''}${improvement}%` : 'Baseline level',
            trendStatus: 'Current trajectory',
        };
    }, [myCompletedAssessments]);

    // Real dynamic data for charts
    const displayData = useMemo(() => {
        if (myCompletedAssessments.length === 0) {
            return {
                trend: [],
                category: [],
                status: [],
            };
        }

        // Real trend data from user's assessments
        const trendData = myCompletedAssessments.map((a, idx) => {
            const dateStr = a.date
                ? new Date(a.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : `Eval ${idx + 1}`;
            const metrics = getAssessmentMetrics(a);
            return {
                name: dateStr,
                score: metrics.percentage,
                baseline: 60,
                type: a.type || 'Evaluation',
            };
        });

        // Real domain breakdown from the latest completed assessment
        const latestAssessment = myCompletedAssessments[myCompletedAssessments.length - 1];
        const categoryData = computeDomainBreakdown(latestAssessment);

        // Real skill matrix distribution
        const statusData = computeSkillMatrix(latestAssessment);

        return {
            trend: trendData,
            category: categoryData,
            status: statusData,
        };
    }, [myCompletedAssessments]);

    // List of questionnaires to show when no direct pending assignments exist
    const availableQuestionnaires = useMemo(() => {
        if (myPendingAssessments.length > 0) return [];
        return patientQuestionnaires.slice(0, 3);
    }, [myPendingAssessments, patientQuestionnaires]);

    return (
        <div className="space-y-8 pb-12 font-inter">
            {/* HERO BANNER - MATCHING MEDIBOOK / NORMAL PORTAL */}
            <div className="relative rounded-[2rem] p-8 md:p-10 overflow-hidden bg-gradient-to-r from-[#4f46e5] via-[#7c3aed] to-[#a855f7] text-white shadow-xl">
                {/* Decorative background blurs */}
                <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-white/10 blur-2xl pointer-events-none" />
                <div className="absolute -bottom-16 right-48 w-48 h-48 rounded-full bg-white/10 blur-xl pointer-events-none" />
                <div className="absolute top-10 right-96 w-28 h-28 rounded-full bg-white/5 blur-lg pointer-events-none" />

                <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 ring-4 ring-emerald-400/25" />
                            <span className="text-[11px] font-black uppercase tracking-widest text-purple-200">
                                PATIENT SCREENING DASHBOARD
                            </span>
                        </div>
                        <h1 className="text-3xl md:text-4xl font-extrabold font-outfit tracking-tight text-white">
                            Welcome back, {user?.name?.split(' ')[0] || 'User'} 👋
                        </h1>
                        <p className="text-purple-100 text-sm md:text-base mt-2 font-normal max-w-xl leading-relaxed">
                            Here's your clinical progression and developmental screening snapshot for today.
                        </p>
                    </div>

                    {/* Quick Stats Pills matching Image 2 */}
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="bg-white/15 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-4 text-center min-w-[105px] shadow-sm">
                            <p className="text-3xl font-black text-white">{stats.total}</p>
                            <p className="text-[10px] uppercase font-bold text-purple-200 tracking-wider mt-0.5">Assessments</p>
                        </div>
                        <div className="bg-white/15 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-4 text-center min-w-[105px] shadow-sm">
                            <p className="text-3xl font-black text-white">{stats.avg || '--'}</p>
                            <p className="text-[10px] uppercase font-bold text-purple-200 tracking-wider mt-0.5">Avg Maturity</p>
                        </div>
                        <div className="bg-white/15 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-4 text-center min-w-[105px] shadow-sm">
                            <p className="text-3xl font-black text-white">{stats.latest || '--'}</p>
                            <p className="text-[10px] uppercase font-bold text-purple-200 tracking-wider mt-0.5">Latest Score</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sub-header with Period Filter */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                <div>
                    <h2 className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">Clinical Markers</h2>
                    <p className="text-slate-400 text-xs font-semibold">Active evaluation progress and milestone ratings</p>
                </div>
                <div className="flex items-center bg-purple-50/80 rounded-2xl p-1.5 border border-purple-100/80">
                    {['Daily', 'Weekly', 'Monthly', 'Yearly'].map((f) => (
                        <button
                            key={f}
                            onClick={() => setTimeFilter(f)}
                            className={`px-5 py-2 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${
                                timeFilter === f
                                    ? 'bg-white text-brand-600 shadow-sm'
                                    : 'text-slate-400 hover:text-slate-600'
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                <StatCard 
                    label="Assessments" 
                    value={stats.total} 
                    icon={<FileText className="text-brand-600" />} 
                    trend={stats.trendTotal}
                    color="bg-brand-50"
                />
                <StatCard 
                    label="Avg Maturity" 
                    value={stats.avg || '--'} 
                    icon={<Activity className="text-brand-600" />} 
                    trend={stats.trendAvg}
                    color="bg-brand-50"
                />
                <StatCard 
                    label="Latest Score" 
                    value={stats.latest || '--'} 
                    icon={<Award className="text-brand-600" />} 
                    trend={stats.trendLatest}
                    color="bg-brand-50"
                />
                <StatCard 
                    label="Improvement" 
                    value={stats.total > 1 ? `${stats.improvement >= 0 ? '+' : ''}${stats.improvement}%` : '0%'} 
                    icon={<TrendingUp className={stats.improvement >= 0 ? "text-emerald-600" : "text-rose-500"} />} 
                    isTrend={stats.total > 1}
                    trendValue={stats.improvement}
                    trend={stats.trendImprovement}
                    color="bg-emerald-50"
                />
                <StatCard 
                    label="Growth Status" 
                    value={stats.status} 
                    icon={<CheckCircle className="text-brand-600" />} 
                    trend={stats.trendStatus}
                    color="bg-brand-50"
                />
            </div>

            {/* Main Analytics Content */}
            <div className="grid lg:grid-cols-3 gap-10">
                {/* Charts Area */}
                <div className="lg:col-span-2 space-y-10">
                    {/* Performance Velocity */}
                    <Card className="border-0 shadow-soft overflow-hidden rounded-[2.5rem] bg-white border border-slate-50">
                        <CardHeader className="p-10 pb-0">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-2xl font-black text-slate-900 font-outfit tracking-tight uppercase">
                                        Performance Velocity
                                    </CardTitle>
                                    <CardDescription className="text-slate-400 font-medium">
                                        {myCompletedAssessments.length > 0
                                            ? `Tracking clinical maturity progression across ${myCompletedAssessments.length} assessment(s)`
                                            : 'Real-time trajectory tracking across developmental evaluations'}
                                    </CardDescription>
                                </div>
                                <div className="h-12 w-12 rounded-2xl bg-slate-50 flex items-center justify-center border border-slate-100 shadow-inner">
                                    <TrendingUp className="h-6 w-6 text-brand-600" />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-10 h-[400px]">
                            {displayData.trend.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={displayData.trend}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis 
                                            dataKey="name" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{fill: '#94a3b8', fontSize: 10, fontWeight: 800}} 
                                            dy={10} 
                                        />
                                        <YAxis 
                                            domain={[0, 100]}
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{fill: '#94a3b8', fontSize: 10, fontWeight: 800}} 
                                        />
                                        <Tooltip 
                                            contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)', padding: '15px 20px' }}
                                            formatter={(value) => [`${value}%`, 'Score']}
                                        />
                                        <Line 
                                            type="monotone" 
                                            dataKey="score" 
                                            stroke="#7c3aed" 
                                            strokeWidth={5} 
                                            dot={{ r: 7, fill: '#7c3aed', strokeWidth: 3, stroke: '#fff' }} 
                                            activeDot={{ r: 9 }} 
                                        />
                                        <Line 
                                            type="monotone" 
                                            dataKey="baseline" 
                                            stroke="#cbd5e1" 
                                            strokeWidth={2} 
                                            strokeDasharray="6 6" 
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-center p-8 bg-slate-50/60 rounded-[2rem] border border-dashed border-slate-200">
                                    <div className="h-16 w-16 bg-white rounded-2xl flex items-center justify-center shadow-soft mb-4">
                                        <Activity className="h-8 w-8 text-brand-600" />
                                    </div>
                                    <h4 className="text-lg font-black text-slate-800 font-outfit uppercase tracking-tight">
                                        No Performance Trajectory Yet
                                    </h4>
                                    <p className="text-slate-400 text-sm max-w-md mt-1 mb-6 font-normal leading-relaxed">
                                        Complete a developmental screening or clinical evaluation below to begin tracking your clinical progression curve with real data.
                                    </p>
                                    <Button 
                                        onClick={() => navigate('/Screening_test/patient/assessment')}
                                        className="h-12 rounded-xl px-8 bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs uppercase tracking-widest shadow-premium"
                                    >
                                        Start First Assessment
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <div className="grid md:grid-cols-2 gap-10">
                        {/* Domain Performance */}
                        <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 overflow-hidden">
                            <CardHeader className="p-8 pb-0">
                                <CardTitle className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">
                                    Domain Breakdown
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-8 h-[320px]">
                                {displayData.category.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={displayData.category} layout="vertical" margin={{ left: 20, right: 30, top: 0, bottom: 0 }}>
                                            <XAxis type="number" domain={[0, 100]} hide />
                                            <YAxis 
                                                dataKey="category" 
                                                type="category" 
                                                axisLine={false} 
                                                tickLine={false} 
                                                tick={{fill: '#94a3b8', fontSize: 10, fontWeight: 800}} 
                                                width={90}
                                            />
                                            <Tooltip 
                                                cursor={{fill: 'transparent'}}
                                                formatter={(val) => [`${val}%`, 'Maturity']}
                                            />
                                            <Bar dataKey="score" fill="#7c3aed" radius={[0, 12, 12, 0]} barSize={22} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center text-center p-6 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                                        <p className="font-bold text-slate-600 text-xs uppercase tracking-wider">No Domain Data</p>
                                        <p className="text-slate-400 text-xs mt-1">Domain benchmarks populate after completing an evaluation.</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Skill Matrix */}
                        <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 overflow-hidden">
                            <CardHeader className="p-8 pb-0">
                                <CardTitle className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">
                                    Skill Matrix
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-8 h-[320px] flex items-center justify-center">
                                {displayData.status.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={displayData.status}
                                                innerRadius={65}
                                                outerRadius={95}
                                                paddingAngle={8}
                                                dataKey="value"
                                            >
                                                {displayData.status.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                                            <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{fontSize: '10px', fontWeight: '800', textTransform: 'uppercase'}} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="h-full w-full flex flex-col items-center justify-center text-center p-6 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                                        <p className="font-bold text-slate-600 text-xs uppercase tracking-wider">No Skill Ratings</p>
                                        <p className="text-slate-400 text-xs mt-1">Skill breakdown will populate from your questionnaire answers.</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Sidebar Content */}
                <div className="space-y-10">
                    {/* Growth Insights */}
                    <Card className="border-0 shadow-premium rounded-[2.5rem] bg-slate-900 text-white overflow-hidden relative">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/10 rounded-full blur-3xl"></div>
                        <CardHeader className="p-10">
                            <div className="flex items-center gap-4 text-brand-400">
                                <div className="h-12 w-12 rounded-2xl bg-white/10 flex items-center justify-center border border-white/10">
                                    <TrendingUp className="h-6 w-6" />
                                </div>
                                <CardTitle className="text-2xl font-black font-outfit uppercase tracking-tight">
                                    Growth Data
                                </CardTitle>
                            </div>
                        </CardHeader>
                        <CardContent className="px-10 pb-10 space-y-8">
                            {myCompletedAssessments.length > 0 ? (
                                <>
                                    <InsightItem 
                                        color="bg-green-400" 
                                        text={`Completed ${stats.total} assessment(s) with an overall maturity average of ${stats.avg}.`} 
                                    />
                                    <InsightItem 
                                        color="bg-brand-400" 
                                        text={`Latest score: ${stats.latest}. Performance classification: ${stats.status}.`} 
                                    />
                                </>
                            ) : (
                                <>
                                    <InsightItem 
                                        color="bg-brand-400" 
                                        text={`Welcome, ${user?.name || 'Patient'}. No clinical assessments have been submitted for your profile yet.`} 
                                    />
                                    <InsightItem 
                                        color="bg-amber-400" 
                                        text="Complete an available screening module below to benchmark developmental milestones and generate personalized growth markers." 
                                    />
                                </>
                            )}
                            
                            {myCompletedAssessments.length > 0 && (
                                <div className="pt-8 border-t border-white/10 mt-6">
                                    <Button 
                                        className="w-full h-14 bg-brand-600 hover:bg-brand-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg transition-all" 
                                        onClick={() => navigate('/Screening_test/patient/analysis')}
                                    >
                                        Full Analysis Report <ChevronRight className="ml-3 h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Pending Assignments / Available Questionnaires */}
                    <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 border-t-[8px] border-t-brand-600 overflow-hidden">
                        <CardHeader className="p-8">
                            <div className="flex items-center justify-between mb-4">
                                <Badge className="bg-amber-50 text-amber-600 border-amber-100 px-4 py-1.5 uppercase tracking-[0.2em] text-[10px] font-black rounded-xl">
                                    {myPendingAssessments.length > 0 ? 'Assigned Tasks' : 'Available Tests'}
                                </Badge>
                            </div>
                            <CardTitle className="text-2xl font-black text-slate-900 font-outfit uppercase tracking-tight">
                                {myPendingAssessments.length > 0 ? 'Assignments' : 'Clinical Screening'}
                            </CardTitle>
                            <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">
                                Modules: {myPendingAssessments.length > 0 ? myPendingAssessments.length : availableQuestionnaires.length}
                            </p>
                        </CardHeader>
                        <CardContent className="px-8 pb-8 pt-0 text-center">
                            {myPendingAssessments.length > 0 ? (
                                <div className="space-y-6">
                                    {myPendingAssessments.map((assignment, index) => (
                                        <div key={index} className="space-y-4">
                                            <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner relative overflow-hidden text-left">
                                                <div className="absolute top-0 right-0">
                                                    <Badge className="bg-brand-600 text-white border-0 rounded-bl-xl rounded-tr-none px-3 py-1 text-[9px] font-black uppercase tracking-widest">
                                                        {assignment.frequency || 'Priority'}
                                                    </Badge>
                                                </div>
                                                <p className="font-black text-slate-900 font-outfit uppercase text-lg mb-3 mt-2">
                                                    {assignment.assessmentTitle}
                                                </p>
                                                <div className="flex items-center gap-6 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                    <span className="flex items-center"><Clock className="mr-2 h-4 w-4" /> {assignment.duration || '~20 mins'}</span>
                                                    <span className="flex items-center"><Activity className="mr-2 h-4 w-4" /> Assigned</span>
                                                </div>
                                            </div>
                                            <Button 
                                                className="w-full h-14 bg-brand-600 hover:bg-brand-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-premium transition-all" 
                                                onClick={() => navigate('/Screening_test/patient/assessment', { state: { requiredType: assignment.assessmentTitle } })}
                                            >
                                                Initialize Form <PlayCircle className="ml-3 h-5 w-5" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : availableQuestionnaires.length > 0 ? (
                                <div className="space-y-6">
                                    {availableQuestionnaires.map((q, idx) => (
                                        <div key={idx} className="space-y-4">
                                            <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner text-left">
                                                <p className="font-black text-slate-900 font-outfit uppercase text-base mb-2">
                                                    {q.title}
                                                </p>
                                                <div className="flex items-center gap-6 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                    <span className="flex items-center"><Clock className="mr-2 h-4 w-4" /> {q.duration || '~15 mins'}</span>
                                                    <span className="flex items-center"><ClipboardCheck className="mr-2 h-4 w-4" /> {q.domains || 4} Domains</span>
                                                </div>
                                            </div>
                                            <Button 
                                                className="w-full h-14 bg-brand-600 hover:bg-brand-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-premium transition-all" 
                                                onClick={() => navigate('/Screening_test/patient/assessment', { state: { requiredType: q.title } })}
                                            >
                                                Initialize Form <PlayCircle className="ml-3 h-5 w-5" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="py-12">
                                    <div className="h-20 w-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-slate-100">
                                        <CheckCircle className="h-10 w-10 text-slate-200" />
                                    </div>
                                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">
                                        All modules up to date
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, icon, trend, color, isTrend, trendValue }) {
    return (
        <Card className="border-0 shadow-soft p-8 flex flex-col justify-between overflow-hidden relative rounded-[2.5rem] hover:shadow-premium hover:-translate-y-1 transition-all group bg-white border border-slate-50">
            <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full ${color} opacity-10 blur-xl group-hover:opacity-20 transition-opacity`}></div>
            <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                    <div className={`h-12 w-12 rounded-2xl ${color} flex items-center justify-center shadow-sm border border-white/50 group-hover:scale-110 transition-transform`}>
                        {icon}
                    </div>
                </div>
                <h4 className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-2">{label}</h4>
                <p className="text-3xl font-black text-slate-900 mb-2 font-outfit">{value}</p>
                <div className="flex items-center gap-2">
                    {isTrend ? (
                        <div className={`flex items-center text-[10px] font-black px-2 py-0.5 rounded-lg ${trendValue >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                            {trendValue >= 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                            {Math.abs(trendValue)}%
                        </div>
                    ) : (
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">{trend}</span>
                    )}
                </div>
            </div>
        </Card>
    );
}

function InsightItem({ color, text }) {
    return (
        <div className="flex gap-5 items-start bg-white/5 p-5 rounded-[2rem] border border-white/5 hover:bg-white/10 transition-colors">
            <div className={`mt-2 h-2.5 w-2.5 rounded-full ${color} shadow-lg shrink-0`}></div>
            <p className="text-sm text-slate-300 leading-relaxed font-light">{text}</p>
        </div>
    );
}
